import { create } from 'zustand';
import { StreamChat } from 'stream-chat';
import { QueryClient } from '@tanstack/react-query';
import { chatApiBasePath, getChatUserToken } from '../core/api/chat';
import { captureError } from '../lib/utils';
import useBlockedUsersStore from './blockedUsersStore';

interface ChatState {
	chatClient: StreamChat | null;
	unreadChatCount: number;
	setChatClient: (
		chatClient: StreamChat,
		queryClient: QueryClient,
		userKey: string,
		clientName: string,
		clientProfilePhotoUrl: string
	) => void;
	disconnectChatClient: (chatClient: StreamChat) => Promise<void>;
}

const useChatStore = create<ChatState>((set) => ({
	chatClient: null,
	unreadChatCount: 0,
	setChatClient: (
		chatClient: StreamChat,
		queryClient: QueryClient,
		userKey: string,
		clientName: string,
		clientProfilePhotoUrl: string
	) => {
		chatClient.on((event) => {
			if (event.total_unread_count >= 0) {
				set({ unreadChatCount: event.total_unread_count });
			}
			if (event.type === 'message.new') {
				const senderId = event.user?.id;
				if (senderId && senderId !== chatClient.userID && event.cid) {
					const isBlocked = useBlockedUsersStore.getState().blockedUserIds.includes(senderId);
					if (isBlocked) {
						chatClient.activeChannels[event.cid]?.markRead().catch(() => {});
					}
				}
			}
		});

		const userData = {
			id: userKey,
			name: clientName,
			image: clientProfilePhotoUrl
		};
		chatClient
			.connectUser(userData, async () => {
				const tokenData = await queryClient.fetchQuery({
					queryKey: [chatApiBasePath, 'Token'],
					queryFn: getChatUserToken,
					staleTime: 0 // Should not cache, chat client will determine when to refresh
				});
				return tokenData;
			})
			.then(() => {
				// Only set client if connection was successful and user is still connected
				if (chatClient.userID) {
					set({ chatClient });
					const totalUnreadCount =
						((chatClient.user as unknown as Record<string, unknown>).total_unread_count as number) || 0;
					set({ unreadChatCount: totalUnreadCount });
				}
			})
			.catch((error) => {
				// Connection failed: log error and ensure client state is reset
				captureError(error, (scope) => {
					scope.setTag('chat-connect-error', 'connectUser');
					scope.setContext('chat', { function: 'connectUser' });
				});
				set({ chatClient: null, unreadChatCount: 0 });
			});
	},
	disconnectChatClient: async (chatClient: StreamChat) => {
		if (chatClient) {
			await chatClient.disconnectUser();
			set({ chatClient: null, unreadChatCount: 0 });
		}
	}
}));

export default useChatStore;
